import { zodResolver } from "@hookform/resolvers/zod";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { TabContext, TabPanel } from "@mui/lab";
import { Button, Grid, Tab, Tabs, TextField } from "@mui/material";
import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";
import { EXCLUDED_PRODUCT_STATUSES_FOR_NEW_BOOKING } from "../../../page/BookingPage/utils";
import { useCreateBookingMutation } from "../../../services/bookingsService";
import { useGetProductsBookedDays, useProductsByIds } from "../../../services/productsService";
import { normalizePercentageForSubmission } from "../../../utils";
import BasicDialog from "../../base/BasicDialog";
import { SnackbarError } from "../../base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../base/SnackbarAlert/SnackbarSuccess";
import { CustomerSelectionFormControl } from "../../form/CustomerSelectionFormControl/CustomerSelectionFormControl";
import { DatePeriodSelector, isPeriodValid } from "../../form/DatePeriodSelector";
import { DiscountFormControl } from "../../form/DiscountFormControl";
import { ProductSelectionFormControl } from "../../form/ProductSelectionFormControl/ProductSelectionFormControl";
import DateService from "../../../services/dateService";

const addBookingFormSchema = z.object({
    customerIds: z.array(z.string().uuid()).min(1, "validation.required"),
    productIds: z.array(z.string().uuid()).min(1, "validation.required"),
    discounts: z.record(z.string().uuid(), z.number().min(-100).max(100)),
    startDate: z.date({ required_error: "validation.required" }).nullable(),
    endDate: z.date({ required_error: "validation.required" }).nullable(),
    note: z.string(),
});

export function AddReservationDialog(props) {
    const { open, onClose, onSuccess, initialCustomerIds, initialProductIds, sourceBookingId, initialDiscounts } = props;
    const [tab, setTab] = useState("clients");

    const today = new Date();
    const initialEndDate = new Date();
    initialEndDate.setDate(today.getDate() + 2);

    const form = useForm({
        resolver: zodResolver(addBookingFormSchema),
        defaultValues: {
            customerIds: initialCustomerIds || [],
            productIds: initialProductIds || [],
            discounts: initialDiscounts || {},
            startDate: today,
            endDate: initialEndDate,
            note: "",
        },
    });

    const {
        control,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors },
    } = form;

    const { mutate: createBooking } = useCreateBookingMutation({
        onSuccess: () => {
            onSuccess?.();
            onClose();
            toast.custom(
                (curToast) => <SnackbarSuccess message={t("bookings.addBookingDialog.success")} />,
                {
                    position: "bottom-right",
                },
            );
        },
        onError: (error) => {
            toast.custom(
                (curToast) => <SnackbarError message={t("bookings.addBookingDialog.error")} />,
                {
                    position: "bottom-right",
                },
            );
        },
    });

    const productIds = useWatch({ control, name: "productIds" });
    const { data: selectedProductsData } = useProductsByIds(productIds, productIds.length > 0);
    const selectedProducts = selectedProductsData || [];
    const bookingDurationLimits = selectedProducts.map(
        (p) => p.investment.invest_booking_duration_days,
    );
    const maxDuration =
        bookingDurationLimits.length > 0 ? Math.min(...bookingDurationLimits) : undefined;

    const onSubmit = (data) => {
        if (!isPeriodValid(data.startDate, data.endDate, excludedDates, maxDuration)) {
            toast.custom(
                (curToast) => <SnackbarError message={t("validation.invalidDatePeriod")} />,
                {
                    position: "bottom-right",
                },
            );
            return;
        }

        createBooking({
            customer_ids: data.customerIds,
            start: DateService.getISOStringZeroHourOfDate(data.startDate),
            end: DateService.getISOStringZeroHourOfDate(data.endDate),
            note: data.note,
            products: Object.entries(data.discounts).map(([productId, discountPercent]) => ({
                product_id: productId,
                discount_percent: normalizePercentageForSubmission(discountPercent),
            })),
            offer_id: props.offerId,
            source_booking: sourceBookingId
        });
    };

    useEffect(() => {
        const currentDiscounts = getValues("discounts");
        const newDiscounts = {};
        productIds.forEach((id) => {
            newDiscounts[id] = currentDiscounts[id] || 0;
        });
        setValue("discounts", newDiscounts);
    }, [productIds, setValue, getValues]);

    const excludedBookings = sourceBookingId ? [sourceBookingId] : null
    const { data: excludedDates = [] } = useGetProductsBookedDays(productIds, excludedBookings, {
        select: (data) =>
            data.booked_days.map(
                (day) =>
                    new Date(
                        new Date(day).getFullYear(),
                        new Date(day).getMonth(),
                        new Date(day).getDate(),
                    ),
            ),
        enabled: productIds.length > 0,
    });

    const customerIds = useWatch({ control, name: "customerIds" });
    const startDate = useWatch({ control, name: "startDate" });
    const endDate = useWatch({ control, name: "endDate" });
    const note = useWatch({ control, name: "note" });

    const handleNext = () => {
        if (tab === "clients") {
            setTab("products");
        } else if (tab === "products") {
            setTab("discounts");
        } else if (tab === "discounts") {
            setTab("booking");
        }
    };

    const handleBack = () => {
        if (tab === "booking") {
            setTab("discounts");
        } else if (tab === "discounts") {
            setTab("products");
        } else if (tab === "products") {
            setTab("clients");
        }
    };

    const isNextDisabled = () => {
        if (tab === "clients" && customerIds.length === 0) {
            return true;
        }
        if (tab === "products") {
            if (productIds.length === 0) {
                return true;
            }

            if (selectedProducts.length > 0) {
                const hasApartment = selectedProducts.some((p) => p.is_apartment);
                const hasOrphan = selectedProducts.some(
                    (p) => !p.is_apartment && p.parent_product == null,
                );

                if (hasOrphan && !hasApartment) {
                    return true;
                }
            }
        }
        if (tab === "discounts") {
            return false; // can always proceed from discounts
        }
        if (tab === "booking") {
            return true; // last tab
        }
        return false;
    };

    const isSaveDisabled = !isPeriodValid(startDate, endDate, excludedDates, maxDuration);

    const onStartDateChange = useCallback(
        (date) => {
            setValue("startDate", date, { shouldValidate: true });
        },
        [setValue],
    );

    const onEndDateChange = useCallback(
        (date) => {
            setValue("endDate", date, { shouldValidate: true });
        },
        [setValue],
    );

    return (
        <BasicDialog
            open={open}
            onClose={onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("bookings.addBookingDialog.title")}
            showDialogActions
            alwaysFullScreen={true}
            contentSx={{ pt: 0 }}
            renderFooter={() => (
                <Grid container justifyContent={"space-between"} gap={2} sx={{ pb: 2, px: 2 }}>
                    <Grid item xs>
                        <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<KeyboardArrowLeftIcon />}
                            disabled={tab === "clients"}
                            onClick={handleBack}
                        >
                            {t("bookings.addBookingDialog.back")}
                        </Button>
                    </Grid>
                    <Grid item xs>
                        {tab === "booking" ? (
                            <Button
                                variant="contained"
                                fullWidth
                                color="success"
                                onClick={handleSubmit(onSubmit)}
                                disabled={isSaveDisabled}
                            >
                                {t("bookings.addBookingDialog.save")}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                fullWidth
                                endIcon={<KeyboardArrowRightIcon />}
                                disabled={isNextDisabled()}
                                onClick={handleNext}
                            >
                                {t("bookings.addBookingDialog.next")}
                            </Button>
                        )}
                    </Grid>
                </Grid>
            )}
        >
            <FormProvider {...form}>
                <TabContext value={tab}>
                    <Tabs
                        variant="fullWidth"
                        value={tab}
                        onChange={(e, newValue) => setTab(newValue)}
                    >
                        <Tab
                            value={"clients"}
                            label={t("bookings.addBookingDialog.chooseClient")}
                        />
                        <Tab
                            disabled={customerIds.length === 0}
                            value={"products"}
                            label={t("bookings.addBookingDialog.chooseProducts")}
                        />
                        <Tab
                            disabled={productIds.length === 0}
                            value="discounts"
                            label={t("bookings.addBookingDialog.discounts")}
                        />
                        <Tab
                            disabled={productIds.length === 0}
                            value="booking"
                            label={t("bookings.addBookingDialog.chooseDates")}
                        />
                    </Tabs>
                    <TabPanel value="clients" sx={{ p: 0, pt: 2 }}>
                        <CustomerSelectionFormControl control={control} fieldName="customerIds" />
                    </TabPanel>
                    <TabPanel value="products" sx={{ p: 0, pt: 2 }}>
                        <ProductSelectionFormControl
                            multipleApartments={false}
                            selectedProductIds={productIds}
                            setSelectedProductIds={(ids) =>
                                setValue("productIds", ids, { shouldValidate: true })
                            }
                            excludedStatuses={EXCLUDED_PRODUCT_STATUSES_FOR_NEW_BOOKING}
                        />
                    </TabPanel>
                    <TabPanel value="discounts" sx={{ p: 0, pt: 2 }}>
                        <DiscountFormControl fields={{ discount: "discounts" }} />
                    </TabPanel>
                    <TabPanel value="booking" sx={{ p: 0, pt: 2 }}>
                        <Grid container spacing={2} direction="column" alignItems="center">
                            <Grid item sx={{ width: "75%" }}>
                                <DatePeriodSelector
                                    startDate={startDate}
                                    endDate={endDate}
                                    excludedDates={excludedDates}
                                    onStartDateChange={onStartDateChange}
                                    onEndDateChange={onEndDateChange}
                                    maxDuration={maxDuration}
                                />
                            </Grid>
                            <Grid item sx={{ width: "75%" }}>
                                <Controller
                                    name="note"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={`${t("bookings.addBookingDialog.form.note")}`}
                                            fullWidth
                                            multiline
                                            rows={4}
                                            error={!!errors.note}
                                            helperText={
                                                errors.note?.message ? t(errors.note.message) : ""
                                            }
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </TabPanel>
                </TabContext>
            </FormProvider>
        </BasicDialog>
    );
}
